import React from 'react';
import { Box, Divider, IconButton, Typography } from '@mui/material';
import { EditOutlined } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import throttle from 'lodash/throttle';
import { PatientUploadedMedicalDocument, customMedicalDocumentUploadReasonLabel } from '@uplift/api';
import { useMedicalDocumentLinkQuery, useMedicalDocumentQuery, useUserQuery } from '@uplift/queries';
import { ReviewLayoutContent, ScrollableView, useReviewLayoutContentControlsContext } from '../../../../../Layout';
import { elementOnScreenPercentage } from '../../../../../Tabs';
import { Tooltip } from '../../../../../Tooltip';
import { Loader } from '../../../../../Loader';
import { useBreakpoint } from '../../../../../hooks';
import { EditDocumentDetailsModal } from '../UploadDocumentsModal/DocumentDetailsForm.component';
import { UploadedDocumentControls } from './UploadedDocumentControls.component';
import { usePDFDocument, PDFPage, PDFPagination } from '../../../../../PDFViewer';


export type PatientUploadedDocumentProps = {
  document: PatientUploadedMedicalDocument;
  editable: boolean;
};


const EditButton: React.VFC<{ disabled: boolean; onClick: () => void }> = ({ disabled, onClick }) => {
  const tooltip = React.useMemo(() => {
    if (disabled) {
      return <span>Only the uploader can edit<br />this document</span>;
    }

    return 'Edit document';
  }, [ disabled ]);

  return (
    <Tooltip title={tooltip} arrow>
      <Box display="flex">
        <IconButton color="secondary" disabled={disabled} onClick={onClick}>
          <EditOutlined />
        </IconButton>
      </Box>
    </Tooltip>
  );
};


export const PatientUploadedDocumentReview: React.VFC<{ patientId: string; medicalDocumentId: string; editable: boolean; showControls?: boolean }> = (props) => {
  const reviewLayoutControlsContext = useReviewLayoutContentControlsContext();
  const [ editing, setEditing ] = React.useState(false);
  const breakpoint = useBreakpoint();
  const queryClient = useQueryClient();
  const { data: medicalDocument } = useMedicalDocumentQuery(props.patientId, props.medicalDocumentId, { suspense: true });
  const { data: provider } = useUserQuery(medicalDocument?.providerId, { suspense: true });
  const { data: downloadLink } = useMedicalDocumentLinkQuery(props.patientId, props.medicalDocumentId);
  const { data: pdf } = usePDFDocument(downloadLink);
  const [ page, setPage ] = React.useState(0);

  const pages = React.useMemo(() => Array.from({ length: pdf?.numPages ?? 0 }).map((_, index) => index), [ pdf ]);
  const pageElementRefs = React.useRef<HTMLDivElement[]>([]);

  React.useEffect(() => {
    const handleScroll = throttle(() => {
      const items = pageElementRefs.current.map((element, index) => ({
        key: index,
        value: elementOnScreenPercentage(element)
      })).sort((a, b) => b.value - a.value);

      if (items.length) {
        setPage(items[0].key);
      }
    }, 300);

    document.addEventListener('scroll', handleScroll, true);
    document.dispatchEvent(new Event('scroll'));

    return () => document.removeEventListener('scroll', handleScroll, true);
  }, []);

  if (!medicalDocument) {
    return null;
  }

  return (
    <Box display="flex" height="100%" flexDirection="row" flex={1}>
      <Box display="flex" height="100%" flexDirection="column" maxWidth="730px" flex="4">
        <ReviewLayoutContent
          title={customMedicalDocumentUploadReasonLabel[medicalDocument.reasonForUpload] ?? medicalDocument.reasonForUpload}
          subtitle={`uploaded by ${provider?.firstName[0]}. ${provider?.lastName}`}
          contentSpacing={false}
          showControls={props.showControls}
        >
          <Box>
            <Box p={2} pr={0} bgcolor="grey.50" display="flex" alignItems="center" justifyContent="space-between">
              <Typography>Description: {medicalDocument.additionalDetails}</Typography>
              <EditButton
                onClick={() => setEditing(true)}
                disabled={!props.editable}
              />
            </Box>
            <Divider />
            <Box pt={2}>
              <Box width="100%" height="100%" display="flex" flexDirection="column" gap="16px">
                {pdf && pages.map(page => (
                  <React.Suspense key={page} fallback={<Loader color="secondary" height="500px" />}>
                    <div
                      ref={el => {
                        if (el) {
                          pageElementRefs.current[page] = el;
                        }
                      }}
                    >
                      <PDFPage pdf={pdf} pageNumber={page} zoomAndDrag={true} />
                    </div>
                  </React.Suspense>
                ))}
              </Box>
            </Box>
            {props.editable && (
              <EditDocumentDetailsModal
                open={editing}
                onClose={() => setEditing(false)}
                onSuccess={async () => {
                  await Promise.all([
                    queryClient.invalidateQueries({ queryKey: [ 'user', medicalDocument.patientId, 'document', medicalDocument.id ] }),
                    queryClient.invalidateQueries({ queryKey: [ 'user', medicalDocument.patientId, 'documents' ] }),
                    queryClient.invalidateQueries({ queryKey: [ 'user', medicalDocument.patientId, 'activity' ] }),
                  ]);
                  setEditing(false);
                }}
                document={medicalDocument}
              />
            )}
          </Box>
        </ReviewLayoutContent>
        <UploadedDocumentControls
          document={medicalDocument}
          onDeleteSuccess={async () => {
            reviewLayoutControlsContext?.onNext?.();
          }}
          pagesLabel={pdf ? `Page ${page + 1}/${pdf?.numPages}` : null}
        />
      </Box>
      <Divider orientation="vertical" />
      {pdf && pdf?.numPages > 1 && breakpoint.md && (
        <ScrollableView height="100%" maxWidth="150px" minWidth="112px">
          <Box display="flex" flex="1" pb={8} pt={3} justifyContent="center">
            <Box maxWidth="70%">
              <PDFPagination
                pdf={pdf}
                pageNumber={page}
                setPage={(page) => {
                  pageElementRefs.current[page]?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
                }}
              />
            </Box>
          </Box>
        </ScrollableView>
      )}
    </Box>
  );
};
